'use client';

import { useState, useEffect } from 'react';
import { useRouter, useSearchParams, usePathname } from 'next/navigation';
import Icon from '@/components/ui/AppIcon';

interface FilterOption {
  id: string;
  label: string;
  count?: number;
}

interface FilterCategory {
  id: string;
  label: string;
  options: FilterOption[];
}

interface CollapsibleFilterPanelProps {
  className?: string;
  allProjects: Array<{
    status: 'active' | 'completed' | 'archived';
    entryCount: number;
    createdAt: string;
    updatedAt: string;
  }>;
}

const CollapsibleFilterPanel = ({ className = '', allProjects }: CollapsibleFilterPanelProps) => {
  const router = useRouter();
  const searchParams = useSearchParams();
  const pathname = usePathname();
  const [isHydrated, setIsHydrated] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [selectedFilters, setSelectedFilters] = useState<Record<string, string[]>>({});
  const [expandedCategories, setExpandedCategories] = useState<string[]>(['status']);

  useEffect(() => {
    setIsHydrated(true);
  }, []);

  // Calculate counts from actual projects
  const statusCounts = allProjects.reduce((acc, project) => {
    acc[project.status] = (acc[project.status] || 0) + 1;
    return acc;
  }, {} as Record<string, number>);

  const entryCountRanges = [
    { min: 0, max: 10, label: '0-10 entries' },
    { min: 11, max: 25, label: '11-25 entries' },
    { min: 26, max: 50, label: '26-50 entries' },
    { min: 51, max: Infinity, label: '50+ entries' },
  ];

  const entryCountCounts = entryCountRanges.reduce((acc, range) => {
    const count = allProjects.filter(p => p.entryCount >= range.min && p.entryCount <= range.max).length;
    acc[range.label] = count;
    return acc;
  }, {} as Record<string, number>);

  const filterCategories: FilterCategory[] = [
    {
      id: 'status',
      label: 'Status',
      options: [
        { id: 'active', label: 'Active', count: statusCounts['active'] || 0 },
        { id: 'completed', label: 'Completed', count: statusCounts['completed'] || 0 },
        { id: 'archived', label: 'Archived', count: statusCounts['archived'] || 0 },
      ],
    },
    {
      id: 'entries',
      label: 'Entry Count',
      options: entryCountRanges.map(range => ({
        id: `${range.min}-${range.max === Infinity ? 'inf' : range.max}`,
        label: range.label,
        count: entryCountCounts[range.label] || 0,
      })),
    },
  ];

  useEffect(() => {
    if (!isHydrated) return;

    const filters: Record<string, string[]> = {};
    const categoryIds = ['status', 'entries'];
    categoryIds.forEach((categoryId) => {
      const paramValue = searchParams.get(categoryId);
      if (paramValue) {
        filters[categoryId] = paramValue.split(',');
      }
    });
    setSelectedFilters(filters);

    // Expand the panel if any filters are active on load
    if (Object.keys(filters).length > 0) {
      setIsExpanded(true);
    }
  }, [searchParams, isHydrated]);

  const togglePanel = () => {
    setIsExpanded(!isExpanded);
  };

  const toggleCategory = (categoryId: string) => {
    setExpandedCategories((prev) =>
      prev.includes(categoryId)
        ? prev.filter((id) => id !== categoryId)
        : [...prev, categoryId]
    );
  };

  const handleFilterToggle = (categoryId: string, optionId: string) => {
    const currentFilters = selectedFilters[categoryId] || [];
    const newFilters = currentFilters.includes(optionId)
      ? currentFilters.filter((id) => id !== optionId)
      : [...currentFilters, optionId];

    const updatedFilters = {
      ...selectedFilters,
      [categoryId]: newFilters,
    };

    if (newFilters.length === 0) {
      delete updatedFilters[categoryId];
    }

    setSelectedFilters(updatedFilters);
    updateURL(updatedFilters);
  };

  const updateURL = (filters: Record<string, string[]>) => {
    const params = new URLSearchParams(searchParams.toString());
    
    // Preserve existing search and sort params
    const search = searchParams.get('search');
    const sort = searchParams.get('sort');
    const view = searchParams.get('view');
    
    // Clear filter params
    filterCategories.forEach((category) => {
      params.delete(category.id);
    });

    // Add new filter params
    Object.entries(filters).forEach(([key, values]) => {
      if (values.length > 0) {
        params.set(key, values.join(','));
      }
    });

    // Restore search, sort, and view params
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    if (view) params.set('view', view);

    const queryString = params.toString();
    router.push(`${pathname}${queryString ? `?${queryString}` : ''}`, {
      scroll: false,
    });
  };

  const handleClearAll = () => {
    const params = new URLSearchParams(searchParams.toString());
    
    // Preserve search, sort, and view params
    const search = searchParams.get('search');
    const sort = searchParams.get('sort');
    const view = searchParams.get('view');
    
    // Clear filter params
    filterCategories.forEach((category) => {
      params.delete(category.id);
    });

    // Restore search, sort, and view params
    if (search) params.set('search', search);
    if (sort) params.set('sort', sort);
    if (view) params.set('view', view);

    const queryString = params.toString();
    router.push(`${pathname}${queryString ? `?${queryString}` : ''}`, {
      scroll: false,
    });
    
    setSelectedFilters({});
  };

  const getActiveFilterCount = () => {
    return Object.values(selectedFilters).reduce(
      (sum, filters) => sum + filters.length,
      0
    );
  };

  if (!isHydrated) {
    return (
      <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:198:6" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="198" data-component-end-line="218" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`bg-card rounded-lg shadow-editorial-sm border border-border mb-8 ${className}`}>
        <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:199:8" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="199" data-component-end-line="205" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22px-6%20py-4%20flex%20items-center%20justify-between%20cursor-pointer%22%7D" className="px-6 py-4 flex items-center justify-between cursor-pointer">
          <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:200:10" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="200" data-component-end-line="203" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
            <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:201:12" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="201" data-component-end-line="201" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="FunnelIcon" size={24} className="text-primary" />
            <h2 data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:202:12" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="202" data-component-end-line="202" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-xl%20text-foreground%22%2C%22textContent%22%3A%22Filters%22%7D" className="font-heading font-semibold text-xl text-foreground">Filters</h2>
          </div>
          <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:204:10" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="204" data-component-end-line="204" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%2C%22className%22%3A%22text-muted-foreground%22%7D" name="ChevronDownIcon" size={20} className="text-muted-foreground" />
        </div>
        <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:206:8" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="206" data-component-end-line="217" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22p-6%20border-t%20border-border%20grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="p-6 border-t border-border grid grid-cols-1 md:grid-cols-2 gap-6">
          {[1, 2].map((i) => (
            <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:208:12" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="208" data-component-end-line="215" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" key={i} className="space-y-3">
              <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:209:14" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="209" data-component-end-line="209" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-6%20bg-muted%20rounded%20animate-pulse%20w-3%2F4%22%7D" className="h-6 bg-muted rounded animate-pulse w-3/4" />
              <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:210:14" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="210" data-component-end-line="214" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%22%7D" className="space-y-2">
                {[1, 2, 3].map((j) => (
                  <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:212:18" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="212" data-component-end-line="212" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22h-8%20bg-muted%20rounded%20animate-pulse%22%7D" key={j} className="h-8 bg-muted rounded animate-pulse" />
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    );
  }

  const activeFilterCount = getActiveFilterCount();

  return (
    <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:225:4" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="225" data-component-end-line="335" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%7D" className={`bg-card rounded-lg shadow-editorial-sm border border-border mb-8 ${className}`}>
      <button data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:226:6" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="226" data-component-end-line="251" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22type%22%3A%22button%22%2C%22className%22%3A%22w-full%20px-6%20py-4%20flex%20items-center%20justify-between%20cursor-pointer%20text-left%22%7D"
        type="button"
        className="w-full px-6 py-4 flex items-center justify-between cursor-pointer text-left"
        onClick={togglePanel}
        aria-expanded={isExpanded}
        aria-controls="filter-panel-content"
      >
        <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:233:8" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="233" data-component-end-line="243" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
          <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:234:10" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="234" data-component-end-line="234" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22FunnelIcon%22%2C%22className%22%3A%22text-primary%22%7D" name="FunnelIcon" size={24} className="text-primary" />
          <h2 data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:235:10" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="235" data-component-end-line="237" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="h2" data-component-content="%7B%22elementName%22%3A%22h2%22%2C%22className%22%3A%22font-heading%20font-semibold%20text-xl%20text-foreground%22%2C%22textContent%22%3A%22Filters%22%7D" className="font-heading font-semibold text-xl text-foreground">
            Filters
          </h2>
          {activeFilterCount > 0 && (
            <span data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:239:12" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="239" data-component-end-line="241" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22px-2.5%20py-0.5%20rounded-full%20bg-primary%20text-primary-foreground%20caption%20font-medium%22%7D" className="px-2.5 py-0.5 rounded-full bg-primary text-primary-foreground caption font-medium">
              {activeFilterCount}
            </span>
          )}
        </div>
        <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:244:8" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="244" data-component-end-line="250" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
          name="ChevronDownIcon"
          size={20}
          className={`text-muted-foreground transition-transform duration-300 ${
            isExpanded ? 'rotate-180' : ''
          }`}
        />
      </button>

      {isExpanded && (
        <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:254:8" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="254" data-component-end-line="333" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22id%22%3A%22filter-panel-content%22%2C%22className%22%3A%22p-6%20border-t%20border-border%22%7D" id="filter-panel-content" className="p-6 border-t border-border">
          <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:255:10" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="255" data-component-end-line="320" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22grid%20grid-cols-1%20md%3Agrid-cols-2%20gap-6%22%7D" className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {filterCategories.map((category) => {
              const isCategoryExpanded = expandedCategories.includes(category.id);
              const categoryFilters = selectedFilters[category.id] || [];

              return (
                <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:261:16" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="261" data-component-end-line="317" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-3%22%7D" key={category.id} className="space-y-3">
                  <button data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:262:18" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="262" data-component-end-line="276" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-between%20text-left%20transition-smooth%20hover%3Atext-primary%22%7D"
                    onClick={() => toggleCategory(category.id)}
                    className="w-full flex items-center justify-between text-left transition-smooth hover:text-primary"
                  >
                    <span data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:266:20" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="266" data-component-end-line="268" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22font-heading%20font-medium%20text-foreground%22%7D" className="font-heading font-medium text-foreground">
                      {category.label}
                    </span>
                    <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:269:20" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="269" data-component-end-line="275" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22ChevronDownIcon%22%7D"
                      name="ChevronDownIcon"
                      size={20}
                      className={`transition-transform duration-300 ${
                        isCategoryExpanded ? 'rotate-180' : ''
                      }`}
                    />
                  </button>

                  {isCategoryExpanded && (
                    <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:279:20" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="279" data-component-end-line="315" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22space-y-2%20pl-1%22%7D" className="space-y-2 pl-1">
                      {category.options.map((option) => {
                        const isSelected = categoryFilters.includes(option.id);

                        return (
                          <label data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:284:26" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="284" data-component-end-line="312" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="label" data-component-content="%7B%22elementName%22%3A%22label%22%2C%22className%22%3A%22flex%20items-center%20justify-between%20py-2%20px-3%20rounded-md%20cursor-pointer%20transition-smooth%20hover%3Abg-muted%20group%22%7D"
                            key={option.id}
                            className="flex items-center justify-between py-2 px-3 rounded-md cursor-pointer transition-smooth hover:bg-muted group"
                          >
                            <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:288:28" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="288" data-component-end-line="306" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22flex%20items-center%20space-x-3%22%7D" className="flex items-center space-x-3">
                              <input data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:289:30" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="289" data-component-end-line="296" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="input" data-component-content="%7B%22elementName%22%3A%22input%22%2C%22type%22%3A%22checkbox%22%2C%22className%22%3A%22w-4%20h-4%20rounded%20border-border%20text-primary%20focus%3Aring-2%20focus%3Aring-primary%20focus%3Aring-offset-2%20transition-smooth%20cursor-pointer%22%7D"
                                type="checkbox"
                                checked={isSelected}
                                onChange={() =>
                                  handleFilterToggle(category.id, option.id)
                                }
                                className="w-4 h-4 rounded border-border text-primary focus:ring-2 focus:ring-primary focus:ring-offset-2 transition-smooth cursor-pointer"
                              />
                              <span data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:297:30" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="297" data-component-end-line="305" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%7D"
                                className={`text-sm transition-smooth ${
                                  isSelected
                                    ? 'text-foreground font-medium'
                                    : 'text-muted-foreground'
                                }`}
                              >
                                {option.label}
                              </span>
                            </div>
                            {option.count !== undefined && (
                              <span data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:308:30" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="308" data-component-end-line="310" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22className%22%3A%22text-sm%20text-muted-foreground%22%7D" className="text-sm text-muted-foreground">
                                {option.count}
                              </span>
                            )}
                          </label>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })}
          </div>

          {activeFilterCount > 0 && (
            <div data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:323:12" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="323" data-component-end-line="331" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="div" data-component-content="%7B%22elementName%22%3A%22div%22%2C%22className%22%3A%22mt-6%20pt-4%20border-t%20border-border%22%7D" className="mt-6 pt-4 border-t border-border">
              <button data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:324:14" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="324" data-component-end-line="330" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="button" data-component-content="%7B%22elementName%22%3A%22button%22%2C%22className%22%3A%22w-full%20flex%20items-center%20justify-center%20space-x-2%20px-4%20py-3%20rounded-md%20border%20border-border%20text-foreground%20font-medium%20transition-smooth%20hover%3Abg-muted%20hover%3A-translate-y-px%22%7D"
                onClick={handleClearAll}
                className="w-full flex items-center justify-center space-x-2 px-4 py-3 rounded-md border border-border text-foreground font-medium transition-smooth hover:bg-muted hover:-translate-y-px"
              >
                <Icon data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:328:16" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="328" data-component-end-line="328" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="Icon" data-component-content="%7B%22elementName%22%3A%22Icon%22%2C%22name%22%3A%22XMarkIcon%22%7D" name="XMarkIcon" size={20} />
                <span data-component-id="src/app/projects-index/components/CollapsibleFilterPanel.tsx:329:16" data-component-path="src/app/projects-index/components/CollapsibleFilterPanel.tsx" data-component-line="329" data-component-end-line="329" data-component-file="CollapsibleFilterPanel.tsx" data-component-name="span" data-component-content="%7B%22elementName%22%3A%22span%22%2C%22textContent%22%3A%22Clear%20All%20Filters%22%7D">Clear All Filters</span>
              </button>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default CollapsibleFilterPanel;

